/**
 * Landing SEO "grand sud" — cible national/régional large :
 *   - retraite spirituelle sud France
 *   - séjour bien-être sud
 *   - retraite méditation sud France
 *   - retraite spirituelle Provence
 *
 * URL : /retraite-spirituelle-sud-france
 * Contenu unique (pas de duplication avec /retraite-spirituelle-drome-provence)
 * qui met l'accent sur l'accessibilité depuis toutes les grandes villes du sud
 * et sur la douceur du climat comme prétexte à la reconnexion.
 */
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Sun, ArrowRight, MapPin, Train, Heart, Users } from "lucide-react";
import axios from "axios";
import { API } from "../lib/api";

const SITE_URL = "https://www.bilan2vie.fr";
// Image fournie par Guillaume — femme en méditation solitaire dans un champ
// de lavande au coucher du soleil, avec village médiéval perché en arrière-plan.
const HERO_IMAGE = "https://customer-assets-lxgj4vgw.emergentagent.net/job_spirituel-wellness/artifacts/xo3yvwsi_meditation%20retraite%20spirituelle%20dro%CC%82me.webp";

export default function RetreatLandingSudFrance() {
  const canonical = `${SITE_URL}/retraite-spirituelle-sud-france`;
  const [nextRetreat, setNextRetreat] = useState(null);

  useEffect(() => {
    axios.get(`${API}/retreats`)
      .then((r) => setNextRetreat((r.data.retreats || [])[0] || null))
      .catch(() => {});
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Retraite spirituelle dans le sud de la France — Bilan 2 Vie",
    description:
      "Retraites spirituelles dans le sud de la France, à la frontière de la Drôme et de la Provence. Accessibles depuis Lyon, Marseille, Montpellier, Grenoble et Genève. Méditation, soins énergétiques, ateliers de reconnexion.",
    url: canonical,
    inLanguage: "fr-FR",
    isPartOf: { "@type": "WebSite", name: "Bilan 2 Vie", url: SITE_URL },
    about: [{ "@type": "Thing", name: "Retraite spirituelle sud France" }],
    provider: { "@type": "LocalBusiness", "@id": `${SITE_URL}/#localbusiness`, name: "Bilan 2 Vie", url: SITE_URL },
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "Séjours & Retraites", item: `${SITE_URL}/retraites` },
      { "@type": "ListItem", position: 3, name: "Retraite spirituelle sud France", item: canonical },
    ],
  };

  return (
    <div className="bg-[#faf6ea] text-[#2a2418] min-h-screen">
      <Helmet>
        <title>Retraite spirituelle sud de la France — Séjour bien-être Provence & Drôme | Bilan 2 Vie</title>
        <meta
          name="description"
          content="Retraite spirituelle dans le sud de la France, entre Drôme et Provence. Accessible depuis Lyon (2h), Marseille (2h30), Montpellier (2h30), Grenoble (1h30), Genève (3h). Séjour animé par Guillaume Bilan 2 Vie. Paiement 3-4x via Klarna."
        />
        <meta
          name="keywords"
          content="retraite spirituelle sud France, séjour spirituel sud, retraite bien-être Provence, retraite méditation sud, séjour ressourcement sud France, retraite spirituelle près de Lyon, retraite spirituelle près de Marseille"
        />
        <meta name="geo.region" content="FR" />
        <meta name="geo.placename" content="Sud de la France" />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Bilan 2 Vie" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:title" content="Retraite spirituelle dans le sud de la France — Bilan 2 Vie" />
        <meta property="og:description" content="Un séjour spirituel accessible depuis toutes les grandes villes du sud. Méditation, soins et ateliers en pleine nature." />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={HERO_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      </Helmet>

      <nav aria-label="Fil d'Ariane" className="max-w-5xl mx-auto px-6 pt-8 text-xs text-[#8b7d5e]">
        <Link to="/" className="hover:text-[#2a2418] transition-colors">Accueil</Link>
        <span className="mx-2">/</span>
        <Link to="/retraites" className="hover:text-[#2a2418] transition-colors">Séjours & Retraites</Link>
        <span className="mx-2">/</span>
        <span className="text-[#2a2418]">Sud de la France</span>
      </nav>

      {/* Hero — image pleine largeur avec titre superposé */}
      <section className="relative w-full min-h-[560px] md:min-h-[620px] flex items-end overflow-hidden">
        <img
          src={HERO_IMAGE}
          alt="Retraite spirituelle dans le sud de la France — femme en méditation dans un champ de lavande au coucher du soleil, avec village provençal perché en arrière-plan"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1610]/85 via-[#2a2418]/40 to-transparent" />
        <div className="relative max-w-5xl mx-auto px-6 pb-16 w-full z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-3xl text-white">
            <div className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-white/85 mb-6">
              <Sun className="w-3.5 h-3.5" /> Sud de la France
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] text-white mb-6 drop-shadow-[0_2px_20px_rgba(0,0,0,0.5)]" data-testid="landing-sud-h1">
              Retraite spirituelle<br />dans le sud de la France
            </h1>
            <p className="text-lg text-white/95 leading-relaxed max-w-2xl drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]">
              Une pause pour te réaligner, dans un cadre baigné de lumière et
              porté par la douceur méridionale. Nos séjours prennent racine à
              la <strong>frontière de la Drôme et de la Provence</strong>, à
              quelques heures des grandes villes du sud.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Accessible depuis */}
      <section className="bg-white border-y border-[#e8dfc6]">
        <div className="max-w-5xl mx-auto px-6 py-20">
          <div className="text-center mb-14">
            <div className="text-[#8b7d5e] text-xs tracking-[0.3em] uppercase mb-4">
              <Train className="w-3 h-3 inline mr-2" /> Accessibilité
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl font-light">À la croisée des chemins du sud</h2>
            <p className="mt-5 text-[#5d5240] max-w-2xl mx-auto">
              La plupart de nos retraitants viennent de loin. Voici, à titre indicatif,
              les temps de trajet depuis les grandes villes.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { city: "Lyon", time: "2h en voiture · 1h20 TGV" },
              { city: "Marseille", time: "2h30 en voiture · 1h40 TGV" },
              { city: "Montpellier", time: "2h30 en voiture" },
              { city: "Grenoble", time: "1h30 en voiture" },
              { city: "Genève", time: "3h en voiture · 3h TGV" },
              { city: "Paris", time: "2h20 TGV jusqu'à Valence" },
              { city: "Toulouse", time: "3h30 en voiture" },
              { city: "Nice", time: "3h30 en voiture" },
            ].map((d, i) => (
              <motion.div
                key={d.city}
                initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="bg-[#f5efdf] border border-[#e8dfc6] rounded-lg p-5 text-center"
              >
                <div className="font-serif text-lg text-[#2a2418] mb-1">{d.city}</div>
                <div className="text-xs text-[#5d5240]">{d.time}</div>
              </motion.div>
            ))}
          </div>
          <p className="text-center text-xs text-[#8b7d5e] italic mt-8">
            Covoiturages organisés entre participants · Récupération gare pour les venus en train
          </p>
        </div>
      </section>

      {/* La philosophie du sud */}
      <section className="max-w-4xl mx-auto px-6 py-24">
        <div className="text-center mb-12">
          <div className="text-[#8b7d5e] text-xs tracking-[0.3em] uppercase mb-4">L'esprit du sud</div>
          <h2 className="font-serif text-3xl sm:text-4xl font-light">Un art de vivre au service de ton chemin</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-10">
          <motion.div initial={{ opacity: 0, x: -15 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <div className="w-12 h-12 rounded-full bg-[#e8dfc6] flex items-center justify-center mb-4">
              <Sun className="w-5 h-5 text-[#8b7d5e]" />
            </div>
            <h3 className="font-serif text-xl mb-3">Une lumière qui apaise</h3>
            <p className="text-sm text-[#5d5240] leading-relaxed">
              Le sud de la France a cette qualité de lumière rare — chaude sans agresser,
              présente sans écraser. Elle invite naturellement à ralentir, à sentir, à respirer plus
              amplement. C'est déjà, en soi, une forme de méditation.
            </p>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 15 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <div className="w-12 h-12 rounded-full bg-[#e8dfc6] flex items-center justify-center mb-4">
              <Heart className="w-5 h-5 text-[#8b7d5e]" />
            </div>
            <h3 className="font-serif text-xl mb-3">Une hospitalité vraie</h3>
            <p className="text-sm text-[#5d5240] leading-relaxed">
              Les tables partagées, les repas de saison cuisinés avec les producteurs locaux,
              les longues discussions au coucher du soleil — l'esprit méridional soutient tout notre programme.
              Chaque <Link to="/retraites" className="underline hover:text-[#2a2418]">retraite spirituelle Bilan 2 Vie</Link> respire cette hospitalité.
            </p>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: -15 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
            <div className="w-12 h-12 rounded-full bg-[#e8dfc6] flex items-center justify-center mb-4">
              <Users className="w-5 h-5 text-[#8b7d5e]" />
            </div>
            <h3 className="font-serif text-xl mb-3">Un petit groupe</h3>
            <p className="text-sm text-[#5d5240] leading-relaxed">
              12 participants maximum. C'est la taille juste pour que chacun·e trouve sa place, que
              les échanges soient possibles, que le silence garde son épaisseur. Ni trop, ni trop peu.
            </p>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 15 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
            <div className="w-12 h-12 rounded-full bg-[#e8dfc6] flex items-center justify-center mb-4">
              <MapPin className="w-5 h-5 text-[#8b7d5e]" />
            </div>
            <h3 className="font-serif text-xl mb-3">Un ancrage local</h3>
            <p className="text-sm text-[#5d5240] leading-relaxed">
              Basé à Montélimar depuis plus de dix ans, Guillaume connaît intimement la région et ses
              lieux de force. Les retraites se déroulent dans des mas ou domaines soigneusement sélectionnés,
              dans un rayon de 30 km autour de la ville.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#2a2418] text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Sparkles className="w-8 h-8 text-[#F5D8A2] mx-auto mb-6" />
          <h2 className="font-serif text-3xl sm:text-4xl font-light mb-6">Envie de tenter l'expérience&nbsp;?</h2>
          <p className="text-white/70 mb-10 max-w-xl mx-auto">
            {nextRetreat
              ? `Prochaine retraite spirituelle dans le sud : ${nextRetreat.title}. Il reste des places, avec paiement en plusieurs fois possible.`
              : "Découvre nos prochaines dates de retraites spirituelles dans le sud de la France."}
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/retraites"
              className="inline-flex items-center gap-2 px-8 py-3 bg-[#F5D8A2] text-[#2a2418] rounded-full text-sm tracking-widest uppercase font-medium hover:bg-white transition-all"
              data-testid="landing-sud-cta-retreats"
            >
              Voir toutes les dates <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/retraite-spirituelle-drome-provence"
              className="inline-flex items-center gap-2 px-8 py-3 border border-white/30 text-white rounded-full text-sm tracking-widest uppercase hover:bg-white/10 transition-all"
              data-testid="landing-sud-cta-drome"
            >
              <MapPin className="w-4 h-4" /> En savoir plus sur la Drôme
            </Link>
          </div>
          <Link
            to="/retraites#temoignages"
            className="inline-flex items-center gap-2 mt-8 text-xs text-white/60 hover:text-[#F5D8A2] transition-colors underline underline-offset-4"
            data-testid="landing-sud-cta-testimonials"
          >
            ⭐ Lire les retours des précédentes participant·es
          </Link>
        </div>
      </section>
    </div>
  );
}
